import type { FitImageResult } from "@/components/ImageUploader/utils";

export type AspectRatio = "square" | "portrait" | "landscape";

export type DragOffset = {
  x: number;
  y: number;
};

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const ZERO_OFFSET: DragOffset = { x: 0, y: 0 };

export const getDragConstraints = (
  aspectRatio: AspectRatio | null,
  fittedImage: FitImageResult,
  targetSize: number
) => {
  if (aspectRatio === "portrait") {
    const minY = -(fittedImage.height - targetSize);
    return {
      minX: 0,
      maxX: 0,
      minY,
      maxY: 0,
    };
  }

  if (aspectRatio === "landscape") {
    const minX = -(fittedImage.width - targetSize);
    return {
      minX,
      maxX: 0,
      minY: 0,
      maxY: 0,
    };
  }

  return {
    minX: 0,
    maxX: 0,
    minY: 0,
    maxY: 0,
  };
};

export const getInitialDragOffset = (
  aspectRatio: AspectRatio | null,
  fittedImage: FitImageResult,
  targetSize: number
): DragOffset => {
  if (aspectRatio === "portrait") {
    const { minY } = getDragConstraints(aspectRatio, fittedImage, targetSize);
    return { x: 0, y: minY / 2 };
  }

  if (aspectRatio === "landscape") {
    const { minX } = getDragConstraints(aspectRatio, fittedImage, targetSize);
    return { x: minX / 2, y: 0 };
  }

  return ZERO_OFFSET;
};

export const clampDragOffset = (
  aspectRatio: AspectRatio | null,
  fittedImage: FitImageResult,
  targetSize: number,
  proposedOffset: DragOffset
): DragOffset => {
  const { minX, maxX, minY, maxY } = getDragConstraints(aspectRatio, fittedImage, targetSize);

  return {
    x: clamp(proposedOffset.x, minX, maxX),
    y: clamp(proposedOffset.y, minY, maxY),
  };
};

export const getScaleFactor = (viewportSize: number, targetSize: number) => {
  if (!targetSize) {
    return 1;
  }

  return viewportSize / targetSize;
};

export const getScaledImageMetrics = (
  fittedImage: FitImageResult,
  dragOffset: DragOffset,
  viewportSize: number,
  targetSize: number
) => {
  const scale = getScaleFactor(viewportSize, targetSize);

  return {
    width: fittedImage.width * scale,
    height: fittedImage.height * scale,
    offsetX: dragOffset.x * scale,
    offsetY: dragOffset.y * scale,
  };
};

export const getFitModeMetrics = (
  fittedImage: FitImageResult,
  viewportSize: number,
  targetSize: number,
  aspectRatio: AspectRatio | null
) => {
  const scale = getScaleFactor(viewportSize, targetSize);
  const originalAspect = fittedImage.originalWidth / fittedImage.originalHeight;

  const containDimensions =
    aspectRatio === "portrait"
      ? {
          width: viewportSize * originalAspect,
          height: viewportSize,
        }
      : aspectRatio === "landscape"
        ? {
            width: viewportSize,
            height: viewportSize / originalAspect,
          }
        : {
            width: viewportSize,
            height: viewportSize,
          };

  // Center the image
  const offsetX = (viewportSize - containDimensions.width) / 2;
  const offsetY = (viewportSize - containDimensions.height) / 2;

  return {
    width: containDimensions.width,
    height: containDimensions.height,
    offsetX,
    offsetY,
    cropOffsetX: offsetX / scale,
    cropOffsetY: offsetY / scale,
    cropWidth: containDimensions.width / scale,
    cropHeight: containDimensions.height / scale,
  };
};
